import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import { useSocialStore } from '@/stores/social';
import { mediaUrl } from '@/hooks/useMediaUrl';
import { api } from '@/lib/api';

interface ChatMessage {
  id: string;
  sender_id: string;
  sender_display_name: string;
  sender_avatar_key: string | null;
  content: string;
  created_at: string;
}

interface ChatWindow {
  conversationId: string;
  userId: string;
  displayName: string;
  username: string;
  avatarKey: string | null;
  messages: ChatMessage[];
  inputText: string;
  loading: boolean;
  unread: number;
  minimized: boolean;
  typingUser: string | null;
  ws: WebSocket | null;
}

export function ChatBar() {
  const { user } = useAuthStore();
  const { onlineFriends, fetchOnlineFriends } = useSocialStore();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [chatWindows, setChatWindows] = useState<ChatWindow[]>([]);
  const chatWindowsRef = useRef(chatWindows);
  chatWindowsRef.current = chatWindows;

  // Poll online friends
  useEffect(() => {
    fetchOnlineFriends();
    const interval = setInterval(fetchOnlineFriends, 30000);
    return () => clearInterval(interval);
  }, []);

  // Cleanup WebSockets on unmount
  useEffect(() => {
    return () => {
      chatWindowsRef.current.forEach((w) => w.ws?.close());
    };
  }, []);

  const openChat = useCallback(async (friend: { id: string; display_name: string; username: string; avatar_key: string | null }) => {
    // Check if already open
    const existing = chatWindowsRef.current.find((w) => w.userId === friend.id);
    if (existing) {
      setChatWindows((prev) =>
        prev.map((w) => w.userId === friend.id ? { ...w, minimized: false, unread: 0 } : w)
      );
      return;
    }

    // Start or find conversation
    try {
      const { conversation_id } = await api.post<{ conversation_id: string }>('/conversations', { user_id: friend.id });

      // Load recent messages
      const messages = await api.get<ChatMessage[]>(`/conversations/${conversation_id}/messages?limit=30`);

      // Connect WebSocket
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${protocol}//${window.location.host}/api/chat/${conversation_id}/websocket?userId=${user?.id}&displayName=${encodeURIComponent(user?.display_name || '')}`;
      const ws = new WebSocket(wsUrl);

      const newWindow: ChatWindow = {
        conversationId: conversation_id,
        userId: friend.id,
        displayName: friend.display_name,
        username: friend.username,
        avatarKey: friend.avatar_key,
        messages: messages.map((m: any) => ({
          id: m.id,
          sender_id: m.sender_id,
          sender_display_name: m.sender_display_name || m.display_name || '',
          sender_avatar_key: m.sender_avatar_key || null,
          content: m.content,
          created_at: m.created_at,
        })),
        inputText: '',
        loading: false,
        unread: 0,
        minimized: false,
        typingUser: null,
        ws,
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'message') {
            setChatWindows((prev) =>
              prev.map((w) => {
                if (w.conversationId !== conversation_id) return w;
                const msg: ChatMessage = {
                  id: data.id || Date.now().toString(),
                  sender_id: data.sender_id,
                  sender_display_name: data.sender_display_name || '',
                  sender_avatar_key: data.sender_avatar_key || null,
                  content: data.content,
                  created_at: data.created_at || new Date().toISOString(),
                };
                return {
                  ...w,
                  messages: [...w.messages, msg],
                  unread: w.minimized ? w.unread + 1 : 0,
                  typingUser: null,
                };
              })
            );
          } else if (data.type === 'typing') {
            setChatWindows((prev) =>
              prev.map((w) => {
                if (w.conversationId !== conversation_id) return w;
                return { ...w, typingUser: data.displayName };
              })
            );
            // Clear typing indicator after 3s
            setTimeout(() => {
              setChatWindows((prev) =>
                prev.map((w) => {
                  if (w.conversationId !== conversation_id) return w;
                  return { ...w, typingUser: null };
                })
              );
            }, 3000);
          }
        } catch {
          // ignore
        }
      };

      ws.onclose = () => {
        setChatWindows((prev) =>
          prev.map((w) => w.conversationId === conversation_id ? { ...w, ws: null } : w)
        );
      };

      setChatWindows((prev) => [...prev, newWindow]);
    } catch {
      // ignore
    }
  }, [user]);

  const closeChat = (userId: string) => {
    setChatWindows((prev) => {
      const w = prev.find((w) => w.userId === userId);
      w?.ws?.close();
      return prev.filter((w) => w.userId !== userId);
    });
  };

  const toggleMinimize = (userId: string) => {
    setChatWindows((prev) =>
      prev.map((w) => w.userId === userId ? { ...w, minimized: !w.minimized, unread: 0 } : w)
    );
  };

  const setInputText = (userId: string, text: string) => {
    setChatWindows((prev) =>
      prev.map((w) => w.userId === userId ? { ...w, inputText: text } : w)
    );
  };

  const sendTyping = (userId: string) => {
    const w = chatWindowsRef.current.find((w) => w.userId === userId);
    if (w?.ws?.readyState === WebSocket.OPEN) {
      w.ws.send(JSON.stringify({ type: 'typing' }));
    }
  };

  const sendMessage = async (userId: string) => {
    const win = chatWindowsRef.current.find((w) => w.userId === userId);
    if (!win || !win.inputText.trim()) return;

    const content = win.inputText.trim();
    setInputText(userId, '');

    try {
      const msg = await api.post<any>(`/conversations/${win.conversationId}/messages`, { content });

      // Add to local messages
      setChatWindows((prev) =>
        prev.map((w) => {
          if (w.userId !== userId) return w;
          return {
            ...w,
            messages: [...w.messages, {
              id: msg.id,
              sender_id: msg.sender_id,
              sender_display_name: msg.sender_display_name || user?.display_name || '',
              sender_avatar_key: msg.sender_avatar_key || user?.avatar_key || null,
              content: msg.content,
              created_at: msg.created_at,
            }],
          };
        })
      );
    } catch {
      // Restore text on failure
      setInputText(userId, content);
    }
  };

  if (!user) return null;

  // Max 3 chat windows on screen
  const visibleWindows = chatWindows.slice(-3);

  return (
    <>
      {/* Chat windows */}
      <div className="fixed bottom-0 right-64 flex gap-1 z-40 items-end">
        {visibleWindows.map((win) => (
          <MiniChatWindow
            key={win.userId}
            win={win}
            myId={user.id}
            onClose={() => closeChat(win.userId)}
            onToggleMinimize={() => toggleMinimize(win.userId)}
            onSend={() => sendMessage(win.userId)}
            onInputChange={(text) => setInputText(win.userId, text)}
            onTyping={() => sendTyping(win.userId)}
          />
        ))}
      </div>

      {/* Chat sidebar tab */}
      <div className="fixed bottom-0 right-0 w-56 z-50 hidden lg:block">
        {/* Tab header */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="w-full bg-vibe-500 text-white text-xs font-bold px-3 py-2 flex items-center justify-between rounded-t-md hover:bg-vibe-600 transition-colors"
        >
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-2 h-2 rounded-full bg-green-400" />
            Chat ({onlineFriends.length})
          </span>
          <span>{sidebarOpen ? '▾' : '▴'}</span>
        </button>

        {/* Friend list dropdown */}
        {sidebarOpen && (
          <div className="bg-white border border-sand-400 border-t-0 max-h-80 overflow-y-auto shadow-lg">
            {onlineFriends.length === 0 ? (
              <p className="text-xs text-sand-500 p-3 text-center">Everyone's offline</p>
            ) : (
              <div className="py-1">
                {onlineFriends.map((friend) => (
                  <button
                    key={friend.id}
                    onClick={() => {
                      openChat(friend);
                      setSidebarOpen(false);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-sand-50 transition-colors text-left"
                  >
                    <div className="relative shrink-0">
                      <img src={mediaUrl(friend.avatar_key)} alt="" className="w-6 h-6 rounded object-cover" />
                      <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 bg-green-500 rounded-full border border-white" />
                    </div>
                    <span className="text-xs font-semibold text-sand-700 truncate">{friend.display_name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

function MiniChatWindow({
  win,
  myId,
  onClose,
  onToggleMinimize,
  onSend,
  onInputChange,
  onTyping,
}: {
  win: ChatWindow;
  myId: string;
  onClose: () => void;
  onToggleMinimize: () => void;
  onSend: () => void;
  onInputChange: (text: string) => void;
  onTyping: () => void;
}) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const typingTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!win.minimized) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [win.messages, win.minimized]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  const handleInput = (text: string) => {
    onInputChange(text);
    // Throttle typing indicator
    if (typingTimeout.current) clearTimeout(typingTimeout.current);
    typingTimeout.current = setTimeout(onTyping, 300);
  };

  return (
    <div className="w-72 bg-white border border-sand-400 rounded-t-md shadow-lg flex flex-col overflow-hidden">
      {/* Header */}
      <div
        className="bg-gradient-to-b from-sand-50 to-sand-100 border-b border-sand-400 px-3 py-1.5 flex items-center gap-2 cursor-pointer"
        onClick={onToggleMinimize}
      >
        <div className="relative shrink-0">
          <img src={mediaUrl(win.avatarKey)} alt="" className="w-5 h-5 rounded object-cover" />
          <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 bg-green-500 rounded-full border border-white" />
        </div>
        <Link
          to={`/profile/${win.username}`}
          onClick={(e) => e.stopPropagation()}
          className="text-xs font-bold text-sand-800 hover:text-vibe-500 truncate flex-1"
        >
          {win.displayName}
        </Link>
        {win.unread > 0 && (
          <span className="bg-red-500 text-white text-[9px] font-bold rounded-full px-1 min-w-[14px] text-center">
            {win.unread}
          </span>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          className="text-sand-400 hover:text-sand-700 text-xs ml-1"
        >
          ✕
        </button>
      </div>

      {/* Messages area */}
      {!win.minimized && (
        <>
          <div className="flex-1 overflow-y-auto p-2 space-y-1.5 max-h-64 min-h-[120px] bg-sand-50">
            {win.messages.length === 0 && (
              <p className="text-center text-[10px] text-sand-400 py-4">Start the conversation ∿</p>
            )}
            {win.messages.map((msg) => {
              const isMe = msg.sender_id === myId;
              return (
                <div key={msg.id} className={`flex gap-1 ${isMe ? 'justify-end' : 'justify-start'}`}>
                  {!isMe && (
                    <img src={mediaUrl(msg.sender_avatar_key)} alt="" className="w-5 h-5 rounded object-cover shrink-0 mt-0.5" />
                  )}
                  <div
                    className={`max-w-[80%] px-2 py-1 rounded-lg text-xs ${
                      isMe
                        ? 'bg-vibe-500 text-white'
                        : 'bg-white border border-sand-300 text-sand-800'
                    }`}
                  >
                    {msg.content}
                  </div>
                </div>
              );
            })}

            {/* Typing indicator */}
            {win.typingUser && (
              <div className="flex gap-1 justify-start">
                <div className="bg-sand-200 px-2 py-1 rounded-lg text-[10px] text-sand-500 italic">
                  {win.typingUser} is typing
                  <span className="animate-pulse">...</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="border-t border-sand-300 p-1.5">
            <input
              value={win.inputText}
              onChange={(e) => handleInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Say something..."
              className="w-full text-xs px-2 py-1.5 border border-sand-300 rounded focus:border-vibe-500 focus:ring-1 focus:ring-vibe-500 outline-none bg-white"
            />
          </div>
        </>
      )}
    </div>
  );
}
